import React, { useState } from 'react';
import * as Animatable from 'react-native-animatable';
import { View, Text, Pressable, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { H4 } from 'tamagui';
import { XStack } from 'tamagui';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';

interface DocumentTemplateHeaderProps {
  documents?: any[];
  totalDocuments?: number;
  activeDocuments?: number;
  completedDocuments?: number;
  inProgressDocuments?: number;
}

const DocumentTemplateHeader = ({
  documents = [],
  totalDocuments,
  activeDocuments,
  completedDocuments,
  inProgressDocuments
}: DocumentTemplateHeaderProps) => {
  const theme = themeSelector();
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  return (
    <Animatable.View
        animation="fadeInDown"
        duration={800}
        style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 20,
          padding: 24,
          marginVertical: 16,
          marginHorizontal: 20,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 20,
          elevation: 10,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: isHeaderCollapsed ? 0 : 20 }}>
          <View style={{
            backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="folder-open"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <H4 style={{
              color: theme ? "#1e293b" : "#f1f5f9",
              fontWeight: '700',
              marginBottom: 4,
              fontSize: 24
            }}>
              Document Management
            </H4>
            {!isHeaderCollapsed && (
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                Manage and organize your documents efficiently
              </Text>
            )}
          </View>
          <Pressable
            onPress={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: theme ? "#f1f5f9" : "#1e293b"
            }}
          >
            <MaterialIcons
              name={isHeaderCollapsed ? "expand-more" : "expand-less"}
              size={24}
              color={theme ? "#64748b" : "#94a3b8"}
            />
          </Pressable>
        </View>

        {/* Collapsible Content */}
        {!isHeaderCollapsed && (
          <>
            {/* Stats Grid */}
            <XStack gap={12} flexWrap="wrap" justifyContent="center" alignItems="center">
          {[
            {
              title: 'Total documents',
              value: (totalDocuments ?? documents.length).toString(),
              icon: 'folder',
              color: theme ? "#0ea5e9" : "#38bdf8",
              bgColor: theme ? "#dbeafe" : "#0c4a6e"
            },
            {
              title: 'Active documents',
              value: (activeDocuments ?? documents.filter(d => d.status === 'active').length).toString(),
              icon: 'play-arrow',
              color: theme ? "#059669" : "#10b981",
              bgColor: theme ? "#d1fae5" : "#064e3b"
            },
            {
              title: 'Completed',
              value: (completedDocuments ?? documents.filter(d => d.status === 'completed').length).toString(),
              icon: 'check-circle',
              color: theme ? "#7c3aed" : "#a78bfa",
              bgColor: theme ? "#ede9fe" : "#312e81"
            },
            {
              title: 'In Progress',
              value: (inProgressDocuments ?? documents.filter(d => d.status === 'in-progress' || !d.status).length).toString(),
              icon: 'schedule',
              color: theme ? "#f59e0b" : "#fbbf24",
              bgColor: theme ? "#fef3c7" : "#451a03"
            }
          ].map((stat, index) => (
            <Animatable.View
              key={index}
              animation="bounceIn"
              delay={index * 200}
              style={{
                flexBasis: Platform.OS === 'web' ? '22%' : '45%',
                flexGrow: 1,
                minWidth: 140,
                maxWidth: Platform.OS === 'web' ? 200 : undefined,
                backgroundColor: stat.bgColor,
                borderRadius: 16,
                padding: 16,
                alignItems: 'center',
                marginBottom: 8,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
              }}
            >
              <MaterialIcons
                name={stat.icon as any}
                size={32}
                color={stat.color}
                style={{ marginBottom: 8 }}
              />
              <Text style={{
                fontSize: 24,
                fontWeight: '800',
                color: stat.color,
                marginBottom: 4
              }}>
                {stat.value}
              </Text>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center'
              }}>
                {stat.title}
              </Text>
            </Animatable.View>
          ))}
        </XStack>
          </>
        )}
      </Animatable.View>
  )
}
export default DocumentTemplateHeader;